import { FC, Suspense, useEffect, useLayoutEffect, useRef } from 'react'
import { createBrowserRouter, Navigate, Outlet, RouterProvider } from 'react-router'
import { useLocation, useMatch } from 'react-router'
import { useIntercom } from 'react-use-intercom'
import { ErrorBoundary, setUser as setSentryUser, wrapCreateBrowserRouterV7 } from '@sentry/react'
import { useAnalytics } from 'use-analytics'
import { QueryParamProvider } from 'use-query-params'
import { useLocalStorage } from 'usehooks-ts'

import { LocalStorageKey } from '@/common/utils/localStorage'
import { Banner } from '@/components/componentLibrary'
import MobilityNavigationLayout from '@/components/layouts/MobilityNavigationLayout'
import NavigationLayoutMobile from '@/components/layouts/NavigationLayoutMobile'
import NavigationLayoutMUI from '@/components/layouts/NavigationLayoutMUI'
import LoadAnimation from '@/components/loadAnimation'
import { internalApi } from '@/modules/api/request'
import { useAuth } from '@/modules/authentication'
import Impersonate from '@/modules/authentication/Impersonate'
import { getImpersonatedUserId } from '@/modules/authentication/impersonation'
import LoginGatekeeper from '@/modules/authentication/LoginGatekeeper'
import MfaEnrollmentGatekeeper from '@/modules/authentication/MfaEnrollmentGatekeeper'
import DashboardLayout from '@/modules/dashboard/components/DashboardLayout'
import LogoutIdleUserTimer from '@/modules/idleTimer/LogoutIdleUserTimer'
import { monitorException } from '@/modules/monitoring'
import { useOrganization } from '@/modules/organization/hooks'
import {
  useGetDigitalSmartZonesLandingPage,
  useGetMobilityLandingPage,
  useGetParkingInsightsLandingPage,
  useGetRegulationsLandingPage,
} from '@/modules/permissions/pageAccessHooks'
import TermsOfServiceGateKeeper from '@/modules/termsOfService/TermsOfServiceGatekeeper'
import UpdateModal from '@/modules/update'
import { useCurrentRegion } from '@/modules/urlRouting/hooks'
import { buildIntercomIdentity } from '@/modules/urlRouting/intercomIdentity'
import { PARAMS, PATHS, SEGMENTS } from '@/modules/urlRouting/paths'
import RegionGatekeeper, {
  NavigateToRegionLandingPage,
} from '@/modules/urlRouting/RegionGatekeeper'
import SyncRegionIdParam from '@/modules/urlRouting/SyncRegionIdParam'
import ReactRouter7Adapter from '@/modules/urlRouting/useQueryParamReactRouter7Adapter'
import { generatePathWithRegionId } from '@/modules/urlRouting/utils'
import { wrapPublicRoute, wrapRoute } from '@/modules/urlRouting/wrapRoute'
import { useCurrentUser } from '@/modules/user/hooks'
import WindowSizeModalMui from '@/modules/windowSize/WindowSizeModalMui'
import LoginPage from '@/pages/login'
import { EditState } from '@/pages/mobility/policies/_policy-id/constants'
import PolicyDetailsEditPage from '@/pages/mobility/policies/_policy-id/edit'
import OauthLoginClosingPage from '@/pages/oauth2/login/close'
import OIDCLoginPage from '@/pages/oidc/login'

const RootProviders: FC = () => (
  <QueryParamProvider adapter={ReactRouter7Adapter}>
    <Outlet />
  </QueryParamProvider>
)

const createBrowserRouterWithSentry = wrapCreateBrowserRouterV7(createBrowserRouter)

export const RootRouter: FC = () => {
  const router = createBrowserRouterWithSentry([
    {
      Component: RootProviders,
      errorElement: (
        <ErrorBoundary
          fallback={
            <Banner
              styleType="high-urgency"
              text="An error has occurred and has been logged. Please reload the page."
              className="empty-state-banner"
            />
          }
        />
      ),
      children: [
        { index: true, Component: LoggedInRoute },
        { path: PATHS.LOGIN, Component: LoginPage },
        { path: PATHS.LOGOUT, Component: Logout },
        { path: PATHS.OIDC.LOGIN, Component: OIDCLoginPage },
        {
          path: PATHS.OAUTH2.LOGIN.CLOSE,
          Component: OauthLoginClosingPage,
        },
        { path: PATHS.IMPERSONATE, Component: Impersonate },
        {
          path: `:${PARAMS.REGION_ID}`,
          Component: LoggedInRoute,
          children: [
            {
              index: true,
              Component: NavigateToRegionLandingPage,
            },
            {
              path: SEGMENTS.MOBILITY,
              Component: MobilityNavigationLayout,
              children: [
                {
                  index: true,
                  Component: NavigateToMobilityLandingPage,
                },
                {
                  path: '*',
                  Component: NavigateToMobilityLandingPage,
                },
                {
                  path: SEGMENTS.ACTIVITY_SUMMARY,
                  lazy: () => wrapRoute('mobility/activity-summary/index'),
                },
                {
                  path: SEGMENTS.MAPS,
                  children: [
                    {
                      index: true,
                      Component: NavigateToMobilityLandingPage,
                    },
                    { path: SEGMENTS.LIVE, lazy: () => wrapRoute('mobility/maps/live') },
                    { path: SEGMENTS['311'], lazy: () => wrapRoute('mobility/maps/311') },
                    {
                      path: SEGMENTS.DISTRIBUTION,
                      lazy: () => wrapRoute('mobility/maps/distribution'),
                    },
                    {
                      path: SEGMENTS.OPERATOR_DROP_OFFS,
                      lazy: () => wrapRoute('mobility/maps/operator-drop-offs'),
                    },
                    { path: SEGMENTS.PARKING, lazy: () => wrapRoute('mobility/maps/parking') },
                    { path: SEGMENTS.ROUTES, lazy: () => wrapRoute('mobility/maps/routes') },
                    { path: SEGMENTS.TRIPS, lazy: () => wrapRoute('mobility/maps/trips') },
                  ],
                },
                {
                  path: SEGMENTS.REPORTING,
                  children: [
                    {
                      index: true,
                      Component: NavigateToMobilityLandingPage,
                    },
                    {
                      path: SEGMENTS.TRIP_COUNTS,
                      lazy: () => wrapRoute('mobility/reporting/trip-counts/index'),
                    },
                    {
                      path: SEGMENTS.MOBILITY_METRICS,
                      lazy: () => wrapRoute('mobility/reporting/mobility-metrics/index'),
                    },
                    {
                      path: SEGMENTS.EXPORT,
                      lazy: () => wrapRoute('mobility/reporting/export'),
                    },
                    {
                      Component: DashboardLayout,
                      children: [
                        {
                          index: true,
                          Component: NavigateToMobilityLandingPage,
                        },
                        {
                          path: SEGMENTS.TRIP_DISTANCE,
                          lazy: () => wrapRoute('mobility/reporting/trip-distance'),
                        },
                        {
                          path: SEGMENTS.TRIP_DURATION,
                          lazy: () => wrapRoute('mobility/reporting/trip-duration'),
                        },
                        {
                          path: SEGMENTS.TIME_OF_DAY,
                          lazy: () => wrapRoute('mobility/reporting/time-of-day'),
                        },
                        {
                          path: SEGMENTS.TRIP_DISTANCE_TECH,
                          lazy: () => wrapRoute('mobility/reporting/trip-distance-by-tech'),
                        },
                        {
                          path: SEGMENTS.TRIP_SPEED_TECH,
                          lazy: () => wrapRoute('mobility/reporting/trip-speed-by-tech'),
                        },
                        {
                          path: SEGMENTS.PARKING_TIME,
                          lazy: () => wrapRoute('mobility/reporting/parking-time'),
                        },
                        {
                          path: SEGMENTS.PARKING_TIME_DISTRICT,
                          lazy: () => wrapRoute('mobility/reporting/parking-time-by-district'),
                        },
                      ],
                    },
                  ],
                },
                {
                  path: SEGMENTS.POLICIES,
                  children: [
                    { index: true, lazy: () => wrapRoute('mobility/policies/index') },
                    {
                      path: `:${PARAMS.POLICY_ID}`,
                      children: [
                        {
                          index: true,
                          lazy: () => wrapRoute('mobility/policies/_policy-id/index'),
                        },
                        {
                          path: SEGMENTS.EDIT,
                          element: <PolicyDetailsEditPage editState={EditState.EDIT} />,
                        },
                      ],
                    },

                    { path: SEGMENTS.CREATE, lazy: () => wrapRoute('mobility/policies/create') },
                    {
                      path: SEGMENTS.DUPLICATE,
                      lazy: () => wrapRoute('mobility/policies/duplicate'),
                    },
                    {
                      path: SEGMENTS.COMPLIANCE,
                      children: [
                        {
                          index: true,
                          lazy: () => wrapRoute('mobility/policies/compliance/index'),
                        },
                        {
                          path: `:${PARAMS.POLICY_ID}`,
                          lazy: () => wrapRoute('mobility/policies/compliance/details'),
                        },
                      ],
                    },
                  ],
                },
                { path: SEGMENTS.INVOICING, lazy: () => wrapRoute('mobility/invoicing') },
                { path: SEGMENTS.GEOGRAPHIES, lazy: () => wrapRoute('mobility/geographies') },
                {
                  path: SEGMENTS.MDS_DATA_HEALTH,
                  lazy: () => wrapRoute('mobility/mds-data-health/index'),
                },
              ],
            },
            {
              path: SEGMENTS.REGULATIONS,
              Component: NavigationLayoutMUI,
              children: [
                {
                  path: SEGMENTS.MAP,
                  lazy: () => wrapRoute('curb/regulations/regulations-map'),
                },
                {
                  path: SEGMENTS.LIBRARY,
                  lazy: () => wrapRoute('curb/regulations/regulations-library'),
                },
                {
                  path: SEGMENTS.GEOGRAPHIES,
                  lazy: () => wrapRoute('curb/geographies'),
                },
                {
                  index: true,
                  Component: NavigateToRegulationsLandingPage,
                },
              ],
            },
            {
              path: SEGMENTS.MANAGEMENT,
              Component: NavigationLayoutMUI,
              children: [
                { path: SEGMENTS.METRICS, lazy: () => wrapRoute('curb/management/metrics') },
                { path: SEGMENTS.ACTIVITY, lazy: () => wrapRoute('curb/management/activity') },
                {
                  path: SEGMENTS.FEES_AND_REVENUE,
                  lazy: () => wrapRoute('curb/management/fees-and-revenue'),
                },
                {
                  path: SEGMENTS.GEOGRAPHIES,
                  lazy: () => wrapRoute('curb/geographies'),
                },
                {
                  index: true,
                  Component: NavigateToDigitalSmartZonesLandingPage,
                },
              ],
            },
            {
              path: SEGMENTS.ELEVATE,
              Component: NavigationLayoutMUI,
              children: [
                {
                  path: SEGMENTS.DASHBOARD,
                  lazy: () => wrapRoute('curb/parking-insights/dashboard'),
                },
                {
                  path: SEGMENTS.ENFORCEMENT,
                  lazy: () => wrapRoute('curb/parking-insights/enforcement'),
                },
                { path: SEGMENTS.REVENUE, lazy: () => wrapRoute('curb/parking-insights/revenue') },
                {
                  path: SEGMENTS.SESSIONS,
                  lazy: () => wrapRoute('curb/parking-insights/sessions'),
                },
                {
                  path: SEGMENTS.PRICING_ENGINE,
                  lazy: () => wrapRoute('curb/parking-insights/pricing-engine'),
                },
                {
                  path: SEGMENTS.OCCUPANCY,
                  lazy: () => wrapRoute('curb/parking-insights/occupancy'),
                },
                {
                  path: SEGMENTS.GEOGRAPHIES,
                  lazy: () => wrapRoute('curb/geographies'),
                },
                {
                  index: true,
                  Component: NavigateToParkingInsightsLandingPage,
                },
              ],
            },

            {
              path: SEGMENTS.MOBILE,
              Component: NavigationLayoutMobile,
              children: [
                {
                  index: true,
                  Component: NavigateToMobilityLandingPage,
                },
                {
                  path: SEGMENTS.LIVE,
                  lazy: () => wrapRoute('mobile/live'),
                },
              ],
            },
            { path: '*', Component: NavigateToRegionLandingPage },
          ],
        },
        {
          path: `:${PARAMS.REGION_ID}/${SEGMENTS.PUBLIC}`,
          children: [
            { path: SEGMENTS.ROUTES, lazy: () => wrapPublicRoute('public/routes') },
            { path: SEGMENTS.REGULATIONS, lazy: () => wrapPublicRoute('public/regulations') },
            { path: SEGMENTS.POLICIES, lazy: () => wrapPublicRoute('public/policies') },
          ],
        },
      ],
    },
  ])
  return <RouterProvider router={router} />
}

const Logout: FC = () => {
  const { signOut } = useAuth()
  signOut()

  return <Navigate to={PATHS.ROOT} replace state={{ from: undefined }} />
}

const LoggedInRoute: FC = () => (
  <LoginGatekeeper>
    <MfaEnrollmentGatekeeper>
      <LogoutIdleUserTimer>
        <Suspense fallback={<LoadAnimation />}>
          <TermsOfServiceGateKeeper>
            <ProtectedRoutes />
          </TermsOfServiceGateKeeper>
        </Suspense>
      </LogoutIdleUserTimer>
    </MfaEnrollmentGatekeeper>
  </LoginGatekeeper>
)

// Identity Intercom was last booted with, or undefined while it has no session. Intercom's
// session outlives `ProtectedRoutes`: `IntercomProvider` sits above the router in `app.tsx`,
// while `/logout` and `/impersonate` are sibling routes of `LoggedInRoute`, so switching
// account unmounts this component. Tracking this per mount would miss every such switch.
let bootedIntercomIdentity: string | undefined

const INTERCOM_TOKEN_ATTEMPTS = 3
const INTERCOM_TOKEN_RETRY_DELAY_MS = 500

/**
 * Booting is refused without this token, and nothing schedules another attempt once the
 * effect settles, so a single transient failure would leave the messenger down for the
 * rest of the session -- taking the "Contact support" actions with it. Retry briefly.
 */
const getIntercomIdentityVerification = async () => {
  for (let attempt = 1; attempt <= INTERCOM_TOKEN_ATTEMPTS; attempt++) {
    try {
      return await internalApi.users.getIntercomMessengerSecurityToken()
    } catch (error) {
      if (attempt === INTERCOM_TOKEN_ATTEMPTS) {
        monitorException(error, { context: 'Intercom identity verification unavailable' })
        return undefined
      }
      await new Promise(resolve => setTimeout(resolve, INTERCOM_TOKEN_RETRY_DELAY_MS * attempt))
    }
  }
  return undefined
}

const ProtectedRoutes: FC = () => {
  const location = useLocation()

  const analytics = useAnalytics()
  const { data: currentUser } = useAuth()
  const { boot: bootIntercom, shutdown: shutdownIntercom, update: updateIntercom } = useIntercom()
  const { data: user } = useCurrentUser()
  const {
    data: { organizationName },
  } = useOrganization(user.organizationId)
  const [previouslyLoggedInUser, setPreviouslyLoggedInUser] = useLocalStorage<{
    providers: undefined | string[]
    email: string | null
  }>(LocalStorageKey.USER, { providers: undefined, email: '' })

  // Undefined whenever Intercom must have no session, which includes while impersonating.
  // Deliberately excludes `organizationName`: a rename should not change the identity,
  // because re-booting can only apply it by shutting the session down first.
  const intercomIdentity = buildIntercomIdentity(currentUser?.uid, user, getImpersonatedUserId())

  // Elevate gets its own AI chat, so the Intercom launcher is hidden there. The session stays
  // booted, so "Contact support" actions can still open the messenger. Matches the bare
  // `/:regionId/elevate` root too, so the launcher does not show while it redirects.
  const hideIntercomLauncher = !!useMatch(PATHS.ELEVATE._ANY)
  // Read by the async boot below, which can finish after the user has changed product.
  // Written after commit, so a render that React discards cannot leave its value here.
  const hideIntercomLauncherRef = useRef(hideIntercomLauncher)
  useLayoutEffect(() => {
    hideIntercomLauncherRef.current = hideIntercomLauncher
  }, [hideIntercomLauncher])

  useEffect(() => {
    if (!intercomIdentity) {
      // Suppressed for this account: tear down a session booted before the switch. Runs
      // before the guard below so a session is torn down even if the account went away.
      // Sentry and analytics still identify, so impersonated sessions stay traceable.
      if (bootedIntercomIdentity) shutdownIntercom()
      bootedIntercomIdentity = undefined
    }

    // Narrowing for `currentUser.uid` below.
    if (!currentUser) return

    let active = true

    const boot = async () => {
      const intercomIdentityVerification = await getIntercomIdentityVerification()
      // A newer run of this effect has superseded this one; that run decides whether a
      // session exists at all, and may suppress rather than boot.
      if (!active) return

      // Fail closed: `undefined` means the token request failed, and an enabled
      // verification without a token would identify the account unverified. Either way,
      // tear down any live session rather than leaving the previous account in it, and
      // leave the identity unset so a later run of this effect retries.
      // A disabled verification is not a failure -- environments without access to the
      // signing secret report `enabled: false` and are expected to boot without a JWT.
      if (
        !intercomIdentityVerification ||
        (intercomIdentityVerification.enabled && !intercomIdentityVerification.jwt)
      ) {
        if (bootedIntercomIdentity) shutdownIntercom()
        bootedIntercomIdentity = undefined
        return
      }

      // `bootIntercom` is a no-op while a session is live, so the previous account's
      // session has to be torn down or it would keep its identity and conversations.
      if (bootedIntercomIdentity) shutdownIntercom()
      bootIntercom({
        email: user.email,
        userId: currentUser.uid,
        company: { companyId: user.organizationId, name: organizationName },
        hideDefaultLauncher: hideIntercomLauncherRef.current,
        ...(intercomIdentityVerification.enabled && intercomIdentityVerification.jwt
          ? { intercomUserJwt: intercomIdentityVerification.jwt }
          : {}),
      })
      bootedIntercomIdentity = intercomIdentity
    }

    // Skip minting an identity verification JWT that the no-op boot would discard.
    if (intercomIdentity && bootedIntercomIdentity !== intercomIdentity) void boot()

    setSentryUser({
      userId: user.id,
      email: user.email,
      organization: organizationName,
    })
    analytics.identify(user.email, {
      userId: user.id,
      organization: organizationName,
    })

    return () => {
      active = false
    }
  }, [
    currentUser,
    user,
    intercomIdentity,
    organizationName,
    bootIntercom,
    shutdownIntercom,
    analytics,
  ])

  // Push a renamed organization to the messenger in place. Re-booting would apply it too,
  // but only by tearing down the session, dropping any conversation the user has open.
  useEffect(() => {
    if (!intercomIdentity || bootedIntercomIdentity !== intercomIdentity) return

    updateIntercom({ company: { companyId: user.organizationId, name: organizationName } })
  }, [intercomIdentity, organizationName, user.organizationId, updateIntercom])

  // Show or hide the launcher when the user moves into or out of Elevate. A boot still in
  // progress picks up the current value from the ref instead.
  useEffect(() => {
    if (!intercomIdentity || bootedIntercomIdentity !== intercomIdentity) return

    updateIntercom({ hideDefaultLauncher: hideIntercomLauncher })
  }, [intercomIdentity, hideIntercomLauncher, updateIntercom])

  // Signing out doesn't reload the page, so without this the messenger stays booted on the
  // login page with the departed user's identity and conversation history.
  useEffect(
    () => () => {
      if (bootedIntercomIdentity) {
        shutdownIntercom()
        bootedIntercomIdentity = undefined
      }
    },
    [shutdownIntercom]
  )

  useEffect(() => {
    analytics.page() // send page view on route change
  }, [location, analytics])

  useEffect(() => {
    if (currentUser && previouslyLoggedInUser.email !== currentUser.email) {
      setPreviouslyLoggedInUser({
        email: currentUser.email,
        providers: currentUser.providerData.map(provider => provider?.providerId),
      })
    }
  }, [currentUser, previouslyLoggedInUser, setPreviouslyLoggedInUser])

  return (
    <div>
      <UpdateModal />
      <RegionGatekeeper>
        <SyncRegionIdParam>
          <WindowSizeModalMui />
          <Outlet />
        </SyncRegionIdParam>
      </RegionGatekeeper>
    </div>
  )
}

const NavigateToMobilityLandingPage: FC = () => {
  const mobilityLandingPage = useGetMobilityLandingPage()

  return <NavigateToLandingPageWrapper landingPage={mobilityLandingPage} />
}

const NavigateToDigitalSmartZonesLandingPage: FC = () => {
  const digitalSmartZonesLandingPage = useGetDigitalSmartZonesLandingPage()

  return <NavigateToLandingPageWrapper landingPage={digitalSmartZonesLandingPage} />
}

const NavigateToParkingInsightsLandingPage: FC = () => {
  const parkingInsightsLandingPage = useGetParkingInsightsLandingPage()

  return <NavigateToLandingPageWrapper landingPage={parkingInsightsLandingPage} />
}

const NavigateToRegulationsLandingPage: FC = () => {
  const regulationsLandingPage = useGetRegulationsLandingPage()

  return <NavigateToLandingPageWrapper landingPage={regulationsLandingPage} />
}

const NavigateToLandingPageWrapper: FC<{ landingPage?: string }> = ({ landingPage }) => {
  const {
    data: { regionId },
  } = useCurrentRegion()

  // No landing page for this product is not the same as having no products at
  // all - a mobility-only org opening /:regionId/elevate still has somewhere to
  // go. Defer to the region landing page, which owns both the
  // permissions-still-loading case and the genuinely-no-products dead end.
  if (!landingPage) return <NavigateToRegionLandingPage />

  return <Navigate replace to={generatePathWithRegionId(landingPage, regionId)} />
}
